import React, { useState, useEffect, useMemo } from 'react';

// Sample data (unchanged)
const initialIncomeData = [
  { voucherNo: '000002999', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'March/2025', comm: '10263.07', tds: '513.13', admin: '513.13', pay: '9236.79' },
  { voucherNo: '000002838', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'February/2025', comm: '15196.61', tds: '759.82', admin: '759.82', pay: '13676.97' },
  { voucherNo: '000002674', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'January/2025', comm: '23232.90', tds: '1116.20', admin: '1116.20', pay: '20091.53' },
  { voucherNo: '000002501', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'December/2024', comm: '25683.25', tds: '1284.16', admin: '1284.16', pay: '23114.95' },
  { voucherNo: '000002337', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'November/2024', comm: '21850.22', tds: '1092.51', admin: '1092.51', pay: '19665.21' },
  { voucherNo: '000002316', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'October/2024', comm: '260.00', tds: '13.00', admin: '13.00', pay: '234.00' },
  { voucherNo: '000002143', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'October/2020', comm: '21335.05', tds: '1066.72', admin: '1066.72', pay: '19201.58' },
  { voucherNo: '000001981', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'September/2024', comm: '27811.75', tds: '1390.62', admin: '1390.62', pay: '25030.59' },
  { voucherNo: '000001830', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'August/2024', comm: '28335.70', tds: '1416.81', admin: '1416.81', pay: '25020.14' },
  { voucherNo: '000001718', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'July/2024', comm: '15115.55', tds: '755.79', admin: '755.79', pay: '13604.02' },
  { voucherNo: '000001583', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'June/2024', comm: '6792.50', tds: '339.63', admin: '339.63', pay: '6113.29' },
  { voucherNo: '000001439', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'May/2024', comm: '10706.25', tds: '535.31', admin: '535.31', pay: '9635.65' },
  { voucherNo: '000001251', agentCode: 'RV1865271', agentName: 'Archana Devi', monthYear: 'April/2024', comm: '9037.80', tds: '451.90', admin: '451.90', pay: '8134.02' },
];

const MonthlyIncome = () => {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // Derived filtered data
  const filteredData = useMemo(() => {
    return initialIncomeData.filter((income) => {
      const matchesSearch =
        income.agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        income.monthYear.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesMonth = monthFilter ? income.monthYear.includes(monthFilter) : true;
      const matchesYear = yearFilter ? income.monthYear.includes(yearFilter) : true;
      return matchesSearch && matchesMonth && matchesYear;
    });
  }, [searchTerm, monthFilter, yearFilter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, monthFilter, yearFilter, pageSize]);

  // Export to CSV (using your provided method)
  const exportToCSV = () => {
    // Prepare CSV headers
    const headers = [
      'S.No.',
      'Voucher No.',
      'Agent Code',
      'Agent Name',
      'Month/Year',
      'Comm.',
      'TDS',
      'Admin',
      'Pay',
    ].join(',');

    // Prepare CSV rows
    const rows = filteredData.map((income, index) => [
      index + 1,
      income.voucherNo,
      income.agentCode,
      income.agentName,
      income.monthYear,
      income.comm,
      income.tds,
      income.admin,
      income.pay,
    ].join(','));

    // Combine headers and rows
    const csvContent = [headers, ...rows].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'monthly_income.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Generate unique months and years for filters
  const uniqueMonths = [...new Set(initialIncomeData.map((item) => item.monthYear.split('/')[0]))];
  const uniqueYears = [...new Set(initialIncomeData.map((item) => item.monthYear.split('/')[1]))].sort().reverse();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-white p-6 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 mb-8 animate-pulse">
          Monthly Income Dashboard
        </h2>

        {/* Card Container */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-gray-200">
          {/* Filter and Search Section */}
          <div className="flex flex-col md:flex-row gap-4 mb-8 items-center">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search by Agent Name or Month/Year..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 pl-10 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition duration-300 text-gray-800 placeholder-gray-400"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="flex-1 p-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition duration-300 text-gray-800"
            >
              <option value="">All Months</option>
              {uniqueMonths.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="flex-1 p-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition duration-300 text-gray-800"
            >
              <option value="">All Years</option>
              {uniqueYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <button
              onClick={exportToCSV}
              className="bg-gradient-to-r from-green-400 to-teal-400 text-white p-3 rounded-lg hover:from-green-500 hover:to-teal-500 transition duration-300 shadow-md flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export CSV
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-400/80 to-indigo-400/80 sticky top-0 z-10">
                <tr>
                  {['Voucher No.', 'Agent Code', 'Agent Name', 'Month/Year', 'Comm.', 'TDS', 'Admin', 'Pay'].map((header) => (
                    <th
                      key={header}
                      className="py-4 px-6 text-left text-sm font-semibold text-white uppercase tracking-wider"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedData.map((income, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-100 transition duration-200 cursor-pointer transform hover:scale-[1.01]"
                  >
                    <td className="py-4 px-6 text-gray-700">{income.voucherNo}</td>
                    <td className="py-4 px-6 text-gray-700">{income.agentCode}</td>
                    <td className="py-4 px-6 text-gray-700">{income.agentName}</td>
                    <td className="py-4 px-6 text-gray-700">{income.monthYear}</td>
                    <td className="py-4 px-6 text-gray-700">{income.comm}</td>
                    <td className="py-4 px-6 text-gray-700">{income.tds}</td>
                    <td className="py-4 px-6 text-gray-700">{income.admin}</td>
                    <td className="py-4 px-6 text-gray-700">{income.pay}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="p-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800"
                >
                  {[5, 10, 20].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 bg-gray-200 rounded-lg text-gray-600 disabled:opacity-50 hover:bg-gray-300 transition duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 bg-gray-200 rounded-lg text-gray-600 disabled:opacity-50 hover:bg-gray-300 transition duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyIncome;